/**
 * #2496 — single source of truth for the hydrostatic-testing measurement
 * plausibility bounds. `customer-testing-form.component.ts` and
 * `start-assement-form.component.ts` (libs/front/admin/manage-bodymeasurements/ui)
 * each hardcoded their own — contradictory — ranges for the same physical
 * measurements; this constant is the one place both forms (and anything else
 * gating the same inputs) read from now.
 *
 * These are client-side plausibility gates, not physiological limits: wide
 * enough to admit any real reading (including junior/youth athletes), tight
 * enough to catch gross typos and wrong-unit entries before they reach the
 * body-composition calculation in calculations.service.ts. They are NOT a
 * substitute for the backend's 2-60% body-fat plausibility gate (#1149).
 */
export const MEASUREMENT_BOUNDS = {
  /**
   * Standing height, in inches. Floor of 48in (4'0") covers junior/youth
   * athletes old enough to be tested but shorter than a typical adult —
   * going lower would stop catching the "weight typed into the height
   * field" class of data-entry error. Ceiling of 96in (8'0") sits above the
   * tallest verified adult height on record (Robert Wadlow, ~8'11", a growth
   * disorder outlier) while still rejecting three-digit entry slips.
   */
  heightIn: { min: 48, max: 96 },
  /**
   * Dry (pre-submersion, clothed) body weight, in pounds. Floor of 50lb
   * covers a small junior athlete; ceiling of 500lb is generous for the
   * heaviest plausible client while still catching an accidental extra
   * digit (e.g. 5,000).
   */
  dryWeightLb: { min: 50, max: 500 },
  /**
   * Underwater ("wet") weight, in kilograms — the net scale reading while
   * fully submerged, consumed by calculateBodyDensity() as
   * `dryWeight / ((dryWeight - wetWeight) / waterDensity - RLV - 0.1)`
   * (calculations.service.ts). Buoyancy makes this reading much lower than
   * dry weight, and it legitimately dips below zero: a low-density
   * (higher-body-fat) client displaces more water than they weigh once the
   * harness/tare offset on the underwater rig is netted out, so the reading
   * can go slightly negative rather than floor at 0. -10kg comfortably
   * covers that case while still rejecting an equipment-fault reading;
   * 40kg covers even a dense, heavily-muscled client's higher net reading
   * without being unbounded. (The formula-citation ADR for the hydrostatic
   * calculation, #2487, is still open — these bounds constrain plausible
   * *inputs* to that formula, not the formula's constants themselves.)
   */
  wetWeightKg: { min: -10, max: 40 },
} as const;
